import React, { useMemo, useEffect, useState, useCallback } from 'react';
import { Text } from '@/components/Text';
import { textVariantsEnum, fontWeightEnum } from '@/components/Text';
import { buttonVariants, Button } from '@/components/Button';
import styles from './Subscription.module.scss';
import { CURRENCY_SYMBOLS, plans } from '@/config/pricing';
import httpClient from '@/config/httpClient';

const billingCycleLabel = (cycle) => {
  switch (cycle) {
    case 'quarterly':
      return 'Quarterly';
    case 'yearly':
      return 'Yearly';
    default:
      return 'Monthly';
  }
};

const perUserPeriodSuffix = (cycle) => {
  switch (cycle) {
    case 'quarterly':
      return 'per user / quarter';
    case 'yearly':
      return 'per user / year';
    default:
      return 'per user / month';
  }
};

/** `testPlans` billingCycle values: month | quarter | year */
const metaBillingToUiCycle = (metaBilling) => {
  switch (metaBilling) {
    case 'quarter':
      return 'quarterly';
    case 'year':
      return 'yearly';
    default:
      return 'monthly';
  }
};

const getPlanPriceForCycle = (plan, cycle, currencyCode) => {
  if (!plan || plan.id === 'enterprise') return null;
  const pricing = plan?.pricing?.[currencyCode] || plan?.pricing?.USD || {};
  switch (cycle) {
    case 'quarterly':
      return pricing.quarterly;
    case 'yearly':
      return pricing.yearly;
    default:
      return pricing.monthly;
  }
};

export const ChangeSubscriptionModal = ({
  newPlan,
  subscriptionData,
  billingCycle,
  currencyCode,
  onClose,
  onConfirm,
}) => {
  const [paymentDetails, setPaymentDetails] = useState(null);

  const getRazorpayPaymentDetails = useCallback(async () => {
    const paymentId = subscriptionData?.lastPayment?.paymentId;
    if (!paymentId) return;
    try {
      const response = await httpClient.post('/getRazorpayPaymentDetails', {
        paymentId,
      });
      if (response.data.success) {
        setPaymentDetails(response.data.data);
      }
    } catch {
      setPaymentDetails(null);
    }
  }, [subscriptionData?.lastPayment?.paymentId]);

  useEffect(() => {
    if (subscriptionData?.lastPayment?.paymentId) {
      getRazorpayPaymentDetails();
    } else {
      setPaymentDetails(null);
    }
  }, [subscriptionData?.lastPayment?.paymentId, getRazorpayPaymentDetails]);
  const seats = useMemo(() => {
    const q = subscriptionData?.seats ?? subscriptionData?.quantity;
    if (q == null || q === '') return 2;
    const n = Number(q);
    return Number.isFinite(n) && n > 0 ? n : 2;
  }, [subscriptionData]);

  const currentPlanMeta = subscriptionData?.planId
    ? plans[subscriptionData.planId]
    : null;

  const currentPlanLabel =
    currentPlanMeta?.label ?? currentPlanMeta?.name ?? 'Current plan';

  const currentUnitPrice =
    currentPlanMeta?.price != null ? Number(currentPlanMeta.price) : null;

  const newUnitPrice = getPlanPriceForCycle(
    newPlan,
    billingCycle,
    currencyCode || subscriptionData?.currency || 'USD'
  );
  const newPlanLabel = newPlan?.name ?? 'New plan';

  const currentTotal =
    currentUnitPrice != null ? currentUnitPrice * seats : null;
  const newTotal = newUnitPrice != null ? Number(newUnitPrice) * seats : null;

  const cycleLabel = billingCycleLabel(billingCycle);
  const periodSuffix = perUserPeriodSuffix(billingCycle);

  const currentUiCycle = currentPlanMeta?.billingCycle
    ? metaBillingToUiCycle(currentPlanMeta.billingCycle)
    : billingCycle;
  const currentPeriodSuffix = perUserPeriodSuffix(currentUiCycle);
  const resolvedCurrencyCode =
    currencyCode || subscriptionData?.currency || 'USD';
  const currencySymbol =
    CURRENCY_SYMBOLS[resolvedCurrencyCode] || CURRENCY_SYMBOLS.USD;

  if (!newPlan) {
    return (
      <div className={styles.changeSubscriptionModal}>
        <div className={styles.modalScrollBody}>
          <Text
            variant={textVariantsEnum.Text_md}
            fontWeight={fontWeightEnum.Medium}
          >
            No plan selected
          </Text>
        </div>
        <div className={styles.buttonsContainer}>
          <Button
            variant={buttonVariants.secondary}
            text="Close"
            onClick={onClose}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.changeSubscriptionModal}>
      <div className={styles.modalScrollBody}>
        <Text
          variant={textVariantsEnum.Header}
          fontWeight={fontWeightEnum.Semibold}
        >
          Change subscription
        </Text>
        <Text
          variant={textVariantsEnum.Text_13}
          fontWeight={fontWeightEnum.Normal}
          className={styles.modalLead}
        >
          Review your current plan, the plan you selected, and billing details
          before confirming. Proration and charges are handled by Razorpay when
          you switch.
        </Text>

        <div className={styles.planCompare}>
          <div className={`${styles.planCard} ${styles.planCardCurrent}`}>
            <Text
              variant={textVariantsEnum.Text_xs}
              fontWeight={fontWeightEnum.Semibold}
              className={`${styles.planCardLabel} ${styles.planCardLabelCurrent}`}
            >
              Current plan
            </Text>
            {currentUnitPrice != null ? (
              <div className={styles.planCardPriceRow}>
                <Text
                  variant={textVariantsEnum.Text_lg}
                  fontWeight={fontWeightEnum.Semibold}
                  className={styles.planCardTitle}
                >
                  {currentPlanLabel}
                </Text>
                <Text
                  variant={textVariantsEnum.Text_xl}
                  fontWeight={fontWeightEnum.Bold}
                  className={styles.planCardPriceAmount}
                >
                  {currencySymbol}
                  {currentUnitPrice}
                </Text>
                <Text
                  variant={textVariantsEnum.Text_13}
                  fontWeight={fontWeightEnum.Medium}
                  className={styles.planCardPriceSuffix}
                >
                  / {currentPeriodSuffix}
                </Text>
              </div>
            ) : (
              <Text
                variant={textVariantsEnum.Text_13}
                fontWeight={fontWeightEnum.Normal}
                className={styles.planCardMeta}
              >
                Price unavailable for this plan id; see your invoices for
                amounts.
              </Text>
            )}
            <Text
              variant={textVariantsEnum.Text_13}
              fontWeight={fontWeightEnum.Normal}
              className={styles.planCardMeta}
            >
              {billingCycleLabel(currentUiCycle)} · {seats} seat
              {seats !== 1 ? 's' : ''}
            </Text>
          </div>

          <div className={`${styles.planCard} ${styles.planCardNew}`}>
            <Text
              variant={textVariantsEnum.Text_xs}
              fontWeight={fontWeightEnum.Semibold}
              className={`${styles.planCardLabel} ${styles.planCardLabelNew}`}
            >
              New plan
            </Text>
            {newPlan?.id === 'enterprise' ? (
              <Text
                variant={textVariantsEnum.Text_13}
                fontWeight={fontWeightEnum.Normal}
                className={styles.planCardMeta}
              >
                Enterprise pricing is arranged with sales; changing to this plan
                may require a different checkout flow.
              </Text>
            ) : newUnitPrice != null ? (
              <div className={styles.planCardPriceRow}>
                <Text
                  variant={textVariantsEnum.Text_lg}
                  fontWeight={fontWeightEnum.Semibold}
                  className={styles.planCardTitle}
                >
                  {newPlanLabel}
                </Text>
                <Text
                  variant={textVariantsEnum.Text_xl}
                  fontWeight={fontWeightEnum.Bold}
                  className={styles.planCardPriceAmount}
                >
                  {currencySymbol}
                  {newUnitPrice}
                </Text>
                <Text
                  variant={textVariantsEnum.Text_13}
                  fontWeight={fontWeightEnum.Medium}
                  className={styles.planCardPriceSuffix}
                >
                  / {periodSuffix}
                </Text>
              </div>
            ) : (
              <Text
                variant={textVariantsEnum.Text_13}
                fontWeight={fontWeightEnum.Normal}
                className={styles.planCardMeta}
              >
                —
              </Text>
            )}
            <Text
              variant={textVariantsEnum.Text_13}
              fontWeight={fontWeightEnum.Normal}
              className={styles.planCardMeta}
            >
              {cycleLabel} · {seats} seat{seats !== 1 ? 's' : ''}
            </Text>
          </div>
        </div>

        <div className={styles.paymentSection}>
          <Text
            variant={textVariantsEnum.Text_xs}
            fontWeight={fontWeightEnum.Semibold}
            className={styles.paymentSectionTitle}
          >
            Payment summary
          </Text>

          <div className={styles.detailGrid}>
            <Text
              variant={textVariantsEnum.Text_13}
              fontWeight={fontWeightEnum.Normal}
              className={styles.detailLabel}
            >
              {' '}
              {paymentDetails?.id
                ? paymentDetails?.method === 'upi'
                  ? 'UPI used for payment'
                  : 'Card used for payment'
                : 'Payment method'}
            </Text>
            <Text
              variant={textVariantsEnum.Text_13}
              fontWeight={fontWeightEnum.Medium}
              className={styles.detailValue}
            >
              {!paymentDetails?.id ? (
                '...loading'
              ) : paymentDetails?.method === 'upi' ? (
                <>{paymentDetails?.upi?.vpa}</>
              ) : (
                <>
                  {'**** **** **** '}
                  {paymentDetails?.card?.last4}
                </>
              )}
            </Text>
            <Text
              variant={textVariantsEnum.Text_13}
              fontWeight={fontWeightEnum.Normal}
              className={styles.detailLabel}
            >
              Billing cycle (new plan)
            </Text>
            <Text
              variant={textVariantsEnum.Text_13}
              fontWeight={fontWeightEnum.Medium}
              className={styles.detailValue}
            >
              {cycleLabel}
            </Text>

            <Text
              variant={textVariantsEnum.Text_13}
              fontWeight={fontWeightEnum.Normal}
              className={styles.detailLabel}
            >
              Seats (quantity)
            </Text>
            <Text
              variant={textVariantsEnum.Text_13}
              fontWeight={fontWeightEnum.Medium}
              className={styles.detailValue}
            >
              {seats}
            </Text>

            <Text
              variant={textVariantsEnum.Text_13}
              fontWeight={fontWeightEnum.Normal}
              className={styles.detailLabel}
            >
              Current plan (est. per cycle)
            </Text>
            <Text
              variant={textVariantsEnum.Text_13}
              fontWeight={fontWeightEnum.Medium}
              className={styles.detailValue}
            >
              {currentTotal != null
                ? `${currencySymbol}${currentTotal.toFixed(2)}`
                : '—'}
            </Text>

            <Text
              variant={textVariantsEnum.Text_13}
              fontWeight={fontWeightEnum.Normal}
              className={styles.detailLabel}
            >
              New plan (est. per cycle)
            </Text>
            <Text
              variant={textVariantsEnum.Text_13}
              fontWeight={fontWeightEnum.Medium}
              className={styles.detailValue}
            >
              {newTotal != null
                ? `${currencySymbol}${newTotal.toFixed(2)}`
                : '—'}
            </Text>
          </div>
          <Text
            variant={textVariantsEnum.Text_xs}
            fontWeight={fontWeightEnum.Normal}
            className={styles.prorationNote}
          >
            Actual charge for this change may differ due to proration, taxes,
            and Razorpay&apos;s calculation at the time of the update. Check
            your Razorpay receipts for the exact amount.
          </Text>
          <Text
            variant={textVariantsEnum.Text_xs}
            fontWeight={fontWeightEnum.Normal}
            className={styles.prorationNote}
          >
            After successfull payment, old subscription will be cancelled
            automatically and new subscription will be created. Refund will be
            initiated for the cancelled subscription.
          </Text>
          <Text
            variant={textVariantsEnum.Text_xs}
            fontWeight={fontWeightEnum.Normal}
            className={styles.prorationNote}
          >
            Clicking on Proceed to pay will for payment screen.
          </Text>
        </div>
      </div>

      <div className={styles.buttonsContainer}>
        <Button
          variant={buttonVariants.secondary}
          text="Cancel"
          onClick={onClose}
        />
        <Button
          variant={buttonVariants.primary}
          text="Proceed to pay"
          onClick={onConfirm}
        />
      </div>
    </div>
  );
};
